import * as React from "react";
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";
import { useShape } from "~/lib/shape-context";

const buttonTextBoxTrimClassName =
  "text-box-trim-both text-box-edge-cap-alphabetic";

const buttonBaseClassNames = [
  "group/button relative isolate inline-flex shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-clip-padding font-medium whitespace-nowrap outline-none select-none",
  buttonTextBoxTrimClassName,
  "glider-focus-ring",
  "disabled:pointer-events-none disabled:cursor-default disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:cursor-default aria-disabled:opacity-50",
  "aria-invalid:ring-1 aria-invalid:ring-destructive/50",
  "[&_svg]:pointer-events-none [&_svg]:shrink-0",
];

const buttonSizeVariants = {
  xs: "h-7 gap-1 px-3 text-caption has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-4",
  sm: "h-8 gap-1 px-3.5 text-meta has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-4",
  md: "h-10 gap-1.5 px-4.5 text-meta has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-4.5",
  default:
    "h-10 gap-1.5 px-4.5 text-meta has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-4.5",
  lg: "h-12 gap-1.5 px-6 text-body has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-5.5",
  "icon-xs":
    "size-7 p-0 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-4",
  "icon-sm":
    "size-8 p-0 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-4",
  icon: "size-10 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-5",
  "icon-lg":
    "size-12 p-0 [&_svg:not([class*='size-']):not([class*='h-']):not([class*='w-'])]:size-5.5",
};

const buttonSurfaceVariants = cva(
  [...buttonBaseClassNames, "transition-colors duration-80"],
  {
    variants: {
      variant: {
        primary: "text-background",
        default: "text-background",
        brand: "text-black",
        inverse: "text-black",
        secondary: "text-foreground",
        tertiary: "border border-border text-foreground",
        outline: "border border-border text-foreground",
        ghost:
          "text-muted-foreground hover:text-foreground data-[active=true]:text-foreground aria-expanded:text-foreground",
        destructive: "text-status-negative-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: buttonSizeVariants,
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

const buttonVariants = cva(
  [
    ...buttonBaseClassNames,
    "transition-[background-color,color,box-shadow,transform] duration-80 ease-out",
  ],
  {
    variants: {
      variant: {
        primary:
          "bg-foreground text-background hover:bg-foreground/90 active:bg-foreground/80 data-[active=true]:bg-foreground/80 aria-expanded:bg-foreground/80",
        default:
          "bg-foreground text-background hover:bg-foreground/90 active:bg-foreground/80 data-[active=true]:bg-foreground/80 aria-expanded:bg-foreground/80",
        brand:
          "bg-brand-mint-200 text-black hover:bg-brand-mint-200/90 active:bg-brand-mint-200/80 data-[active=true]:bg-brand-mint-200/80 aria-expanded:bg-brand-mint-200/80",
        inverse:
          "bg-white text-black hover:bg-white/90 active:bg-white/80 data-[active=true]:bg-white/80 aria-expanded:bg-white/80",
        secondary:
          "bg-accent text-foreground hover:bg-accent/80 active:bg-accent data-[active=true]:bg-accent aria-expanded:bg-accent",
        tertiary:
          "border border-border bg-transparent text-foreground hover:bg-hover active:bg-active data-[active=true]:bg-active aria-expanded:bg-active",
        outline:
          "border border-border bg-transparent text-foreground hover:bg-hover active:bg-active data-[active=true]:bg-active aria-expanded:bg-active",
        ghost:
          "bg-transparent text-muted-foreground hover:bg-hover hover:text-foreground active:bg-active data-[active=true]:bg-active data-[active=true]:text-foreground aria-expanded:bg-active aria-expanded:text-foreground",
        destructive:
          "bg-destructive/10 text-status-negative-foreground hover:bg-destructive/20 active:bg-destructive/25 data-[active=true]:bg-destructive/25 dark:bg-destructive/20 dark:hover:bg-destructive/30",
        link: "bg-transparent text-primary underline-offset-4 hover:underline",
      },
      size: buttonSizeVariants,
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

type ButtonIconProps = {
  "aria-hidden"?: React.AriaAttributes["aria-hidden"];
  className?: string;
  size?: number | string;
  strokeWidth?: number | string;
};

type ButtonIconComponent = React.ComponentType<ButtonIconProps>;

type ButtonProps = Omit<ButtonPrimitive.Props, "render"> &
  VariantProps<typeof buttonVariants> & {
    active?: boolean;
    asChild?: boolean;
    leadingIcon?: ButtonIconComponent;
    loading?: boolean;
    loadingLabel?: React.ReactNode;
    render?: ButtonPrimitive.Props["render"];
    trailingIcon?: ButtonIconComponent;
  };

type ButtonVariant = NonNullable<ButtonProps["variant"]>;

const buttonBgVariants: Record<ButtonVariant, string> = {
  primary:
    "bg-foreground group-hover/button:bg-foreground/90 group-active/button:bg-foreground/80 group-data-[active=true]/button:bg-foreground/80 group-aria-[expanded=true]/button:bg-foreground/80",
  default:
    "bg-foreground group-hover/button:bg-foreground/90 group-active/button:bg-foreground/80 group-data-[active=true]/button:bg-foreground/80 group-aria-[expanded=true]/button:bg-foreground/80",
  // v1's money CTA: bright mint with black text in BOTH themes (black on
  // mint keeps contrast on light surfaces, so no theme-aware token needed).
  brand:
    "bg-brand-mint-200 group-hover/button:bg-brand-mint-200/90 group-active/button:bg-brand-mint-200/80 group-data-[active=true]/button:bg-brand-mint-200/80 group-aria-[expanded=true]/button:bg-brand-mint-200/80",
  inverse:
    "bg-white group-hover/button:bg-white/90 group-active/button:bg-white/80 group-data-[active=true]/button:bg-white/80 group-aria-[expanded=true]/button:bg-white/80",
  secondary:
    "bg-accent group-hover/button:bg-accent/80 group-active/button:bg-accent group-data-[active=true]/button:bg-accent group-aria-[expanded=true]/button:bg-accent",
  tertiary:
    "bg-transparent group-hover/button:bg-hover group-active/button:bg-active group-data-[active=true]/button:bg-active group-aria-[expanded=true]/button:bg-active",
  outline:
    "bg-transparent group-hover/button:bg-hover group-active/button:bg-active group-data-[active=true]/button:bg-active group-aria-[expanded=true]/button:bg-active",
  ghost:
    "bg-transparent group-hover/button:bg-hover group-active/button:bg-active group-data-[active=true]/button:bg-active group-aria-[expanded=true]/button:bg-active",
  destructive:
    "bg-destructive/10 group-hover/button:bg-destructive/20 group-active/button:bg-destructive/25 group-data-[active=true]/button:bg-destructive/25 group-aria-[expanded=true]/button:bg-destructive/25 dark:bg-destructive/20 dark:group-hover/button:bg-destructive/30",
  link: "bg-transparent",
};

const buttonActiveBgVariants: Record<ButtonVariant, string> = {
  primary: "bg-foreground/80",
  default: "bg-foreground/80",
  brand: "bg-brand-mint-200/80",
  inverse: "bg-white/80",
  secondary: "bg-accent",
  tertiary: "bg-active",
  outline: "bg-active",
  ghost: "bg-active",
  destructive: "bg-destructive/25",
  link: "bg-transparent",
};

function getButtonIconSize(size: ButtonProps["size"]): ButtonIconProps["size"] {
  switch (size) {
    case "xs":
    case "sm":
    case "icon-xs":
    case "icon-sm":
      return 16;
    case "lg":
    case "icon-lg":
      return 22;
    case "default":
    case "md":
    case "icon":
    case null:
    case undefined:
      return 18;
  }
}

function renderButtonLabel(children: React.ReactNode) {
  return (
    <span className="inline-flex min-w-0 items-center justify-center gap-[inherit]">
      {children}
    </span>
  );
}

const Button = React.forwardRef<
  React.ElementRef<typeof ButtonPrimitive>,
  ButtonProps
>(function Button(
  {
    active = false,
    asChild = false,
    children,
    className,
    disabled,
    leadingIcon: LeadingIcon,
    loading = false,
    loadingLabel,
    render,
    size = "md",
    trailingIcon: TrailingIcon,
    variant = "primary",
    ...props
  },
  ref,
) {
  const asChildElement =
    asChild && React.isValidElement(children)
      ? (React.Children.only(children) as React.ReactElement<{
          children?: React.ReactNode;
        }>)
      : null;
  const resolvedChildren = asChildElement
    ? asChildElement.props.children
    : children;
  const resolvedRender = asChildElement ? asChildElement : render;
  const shouldUseNativeButton = !(
    typeof resolvedRender !== "undefined" &&
    (!React.isValidElement(resolvedRender) || resolvedRender.type !== "button")
  );
  const isIconOnly =
    size === "icon" ||
    size === "icon-xs" ||
    size === "icon-sm" ||
    size === "icon-lg";
  const iconSize = getButtonIconSize(size);
  const isDisabled = disabled || loading;
  const shape = useShape();
  const resolvedVariant = variant ?? "primary";
  const backgroundClass = active
    ? buttonActiveBgVariants[resolvedVariant]
    : buttonBgVariants[resolvedVariant];

  return (
    <ButtonPrimitive
      {...props}
      ref={ref}
      aria-busy={loading || undefined}
      data-active={active ? "true" : undefined}
      data-slot="button"
      disabled={isDisabled}
      className={`${cn(
        buttonSurfaceVariants({ variant, size }),
        shape.button,
        className,
      )} ${buttonTextBoxTrimClassName}`}
      nativeButton={shouldUseNativeButton}
      render={resolvedRender}
    >
      <span
        aria-hidden="true"
        className={cn(
          "glider-button-press-bg absolute rounded-[inherit]",
          backgroundClass,
        )}
        data-slot="button-background"
      />
      <span className="glider-button-press-label relative inline-grid min-w-0 items-center justify-center gap-[inherit]">
        {loadingLabel ? (
          <span
            aria-hidden="true"
            className="invisible col-start-1 row-start-1 inline-flex items-center gap-[inherit]"
          >
            {loadingLabel}
            <span className="size-8" />
          </span>
        ) : null}
        <span className="col-start-1 row-start-1 inline-flex min-w-0 items-center justify-center gap-[inherit]">
          {loading ? (
            <>
              <span
                aria-hidden={loadingLabel ? true : undefined}
                className="flex min-w-0 items-center justify-center gap-[inherit] opacity-0"
              >
                {LeadingIcon && !isIconOnly ? (
                  <LeadingIcon
                    aria-hidden={true}
                    data-icon="inline-start"
                    size={iconSize}
                    strokeWidth={1.5}
                  />
                ) : null}
                {renderButtonLabel(resolvedChildren)}
                {TrailingIcon && !isIconOnly ? (
                  <TrailingIcon
                    aria-hidden={true}
                    data-icon="inline-end"
                    size={iconSize}
                    strokeWidth={1.5}
                  />
                ) : null}
              </span>
              <span className="absolute inset-0 flex items-center justify-center gap-[inherit]">
                {loadingLabel}
                <svg
                  aria-hidden="true"
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    className="fluid-button-spinner-path"
                    d="M 12 12 C 14 8.5 19 8.5 19 12 C 19 15.5 14 15.5 12 12 C 10 8.5 5 8.5 5 12 C 5 15.5 10 15.5 12 12 Z"
                    pathLength="100"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="1.125"
                  />
                </svg>
              </span>
            </>
          ) : isIconOnly ? (
            <span className="inline-flex items-center justify-center [&_svg]:stroke-[1.5] [&_svg]:transition-[stroke-width] [&_svg]:duration-80 group-hover/button:[&_svg]:stroke-[2]">
              {resolvedChildren}
            </span>
          ) : (
            <>
              {LeadingIcon ? (
                <LeadingIcon
                  aria-hidden={true}
                  className="transition-[stroke-width] duration-80 group-hover/button:stroke-[2]"
                  data-icon="inline-start"
                  size={iconSize}
                  strokeWidth={1.5}
                />
              ) : null}
              {renderButtonLabel(resolvedChildren)}
              {TrailingIcon ? (
                <TrailingIcon
                  aria-hidden={true}
                  className="transition-[stroke-width] duration-80 group-hover/button:stroke-[2]"
                  data-icon="inline-end"
                  size={iconSize}
                  strokeWidth={1.5}
                />
              ) : null}
            </>
          )}
        </span>
      </span>
    </ButtonPrimitive>
  );
});

Button.displayName = "Button";

export { Button, buttonVariants };
export type { ButtonIconComponent, ButtonProps };
